<template>
  <main class="shell">
    <section class="workspace">
      <header class="topbar">
        <div>
          <p class="eyebrow">Chronos Dominion MVP</p>
          <h1>Mission Proof Console</h1>
        </div>
        <div class="subject-control">
          <label for="subject">Auth subject</label>
          <input id="subject" v-model="subject" :disabled="controlsLocked" />
        </div>
      </header>

      <section class="status-grid">
        <div class="metric">
          <span>Player</span>
          <strong>{{ playerState?.profile?.displayName ?? 'Uncreated' }}</strong>
        </div>
        <div class="metric">
          <span>Level</span>
          <strong>{{ playerState?.progression.level ?? '-' }}</strong>
        </div>
        <div class="metric">
          <span>XP</span>
          <strong>{{ playerState?.progression.xp ?? 0 }} / 100</strong>
        </div>
        <div class="metric">
          <span>Credits</span>
          <strong>{{ playerState?.balances.credits ?? '-' }}</strong>
        </div>
        <div class="metric">
          <span>Goods A</span>
          <strong>{{ goodsAStored }} / {{ storageCapacity }}</strong>
        </div>
        <div class="metric">
          <span>Starter Ship</span>
          <strong>{{ starterCapacity }} units</strong>
        </div>
        <div class="metric">
          <span>API</span>
          <strong>{{ apiLabel }}</strong>
        </div>
        <div class="metric">
          <span>Storage Mode</span>
          <strong>{{ storageMode }}</strong>
        </div>
        <div class="metric">
          <span>Index</span>
          <strong>{{ indexMode }}</strong>
        </div>
      </section>

      <section class="flow">
        <div class="panel">
          <div class="panel-header">
            <h2>Player Setup</h2>
            <span class="version">v{{ playerState?.version ?? 0 }}</span>
          </div>
          <div class="actions">
            <button :disabled="controlsLocked || !!playerState" @click="bootstrapPlayer">Create Player</button>
            <button :disabled="controlsLocked || !playerState || !!playerState.profile" @click="setupProfile">Set Profile</button>
            <button :disabled="controlsLocked" @click="loadState">Refresh State</button>
            <button class="danger" :disabled="controlsLocked" @click="deleteAccount">Delete Account</button>
          </div>
          <dl class="state-list">
            <div>
              <dt>Operator tag</dt>
              <dd>{{ playerState?.profile?.operatorTag ?? '-' }}</dd>
            </div>
            <div>
              <dt>Unlocked areas</dt>
              <dd>{{ playerState?.progression.unlockedAreas.join(', ') ?? '-' }}</dd>
            </div>
            <div>
              <dt>Latest event</dt>
              <dd class="hash">{{ playerState?.proof.latestEventHash ?? '-' }}</dd>
            </div>
          </dl>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Storage & Delivery</h2>
            <span class="version">{{ storageUsed }} / {{ storageCapacity }} units</span>
          </div>
          <div class="storage-meter" aria-label="Stored goods capacity">
            <span :style="{ width: `${storagePercent}%` }"></span>
          </div>
          <div class="logistics-grid">
            <div>
              <span>Goods A held</span>
              <strong>{{ goodsAStored }}</strong>
            </div>
            <div>
              <span>Starter ship capacity</span>
              <strong>{{ starterCapacity }}</strong>
            </div>
            <div>
              <span>Delivery goal</span>
              <strong>{{ deliveryProgress.delivered }} / {{ deliveryProgress.goal }}</strong>
            </div>
            <div>
              <span>Dispatchable now</span>
              <strong>{{ dispatchableGoodsA }}</strong>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Mission Selection</h2>
            <button class="small" :disabled="controlsLocked || !playerState" @click="listMissions">Load Missions</button>
          </div>
          <div class="mission-list">
            <button
              v-for="mission in missions"
              :key="mission.missionId"
              class="mission"
              :class="{ selected: mission.missionId === selectedMissionId }"
              @click="selectedMissionId = mission.missionId"
            >
              <span>
                <strong>
                  {{ mission.missionId }}
                  <em v-if="mission.generated">generated</em>
                </strong>
                <small>{{ mission.area }} · {{ mission.durationSeconds }}s · {{ missionSummary(mission) }}</small>
              </span>
              <span class="cost">{{ missionEconomy(mission) }}</span>
            </button>
          </div>
          <div class="actions">
            <button :disabled="!canStart" @click="startMission">{{ startActionLabel }}</button>
            <button :disabled="!canClaim" @click="claimMission">{{ claimActionLabel }}</button>
            <button :disabled="!canRunFive" @click="runFiveMissions">Run 5 Mission Loop</button>
          </div>
          <div class="timer">
            <span>{{ activeMission ? activeMissionSummary : 'No active mission' }}</span>
            <strong>{{ activeMission ? `${remainingSeconds}s` : '-' }}</strong>
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Proof Replay</h2>
            <button class="small" :disabled="controlsLocked || !playerState" @click="verifyReplay">Verify</button>
          </div>
          <div class="proof-row">
            <span>Replay status</span>
            <strong :class="{ ok: replay?.verified }">{{ replay ? (replay.verified ? 'Verified' : 'Failed') : '-' }}</strong>
          </div>
          <div class="proof-row">
            <span>Events replayed</span>
            <strong>{{ replay?.eventCount ?? '-' }}</strong>
          </div>
          <div class="proof-row">
            <span>Final level</span>
            <strong>{{ replay?.finalState?.progression.level ?? '-' }}</strong>
          </div>
          <p class="message">{{ message }}</p>
        </div>
      </section>
    </section>

    <aside class="explorer">
      <div class="explorer-header">
        <h2>OpenCALL Explorer</h2>
        <button class="small" :disabled="calls.length === 0" @click="clearCalls">Clear</button>
      </div>
      <div v-if="calls.length === 0" class="empty">Call an operation to inspect request and response payloads.</div>
      <template v-else>
        <div class="call-list">
          <button
            v-for="call in orderedCalls"
            :key="call.id"
            :class="{ selected: selectedCallId === call.id }"
            @click="selectedCallId = call.id"
          >
            <span>{{ call.request.body.op }}</span>
            <strong>{{ call.response?.status ?? '...' }}</strong>
          </button>
        </div>
        <div v-if="selectedCall" class="payloads">
          <h3>Request</h3>
          <pre>{{ JSON.stringify(selectedCall.request, null, 2) }}</pre>
          <h3>Response</h3>
          <pre>{{ JSON.stringify(selectedCall.response, null, 2) }}</pre>
        </div>
      </template>
    </aside>
  </main>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

type Balance = { credits: number; premium: number };
type MissionLogistics =
  | { type: 'collection'; resourceId: string; quantity: number }
  | { type: 'delivery'; resourceId: string; goal: number };
type MissionDefinition = {
  missionId: string;
  area: string;
  durationSeconds: number;
  cost: Partial<Balance>;
  reward: Partial<Balance> & { xp: number };
  requirements: { level: number };
  logistics?: MissionLogistics;
  generated: boolean;
  signature: string;
};
type MissionInstance = {
  missionInstanceId: string;
  missionId: string;
  startedAt: number;
  endsAt: number;
  definition: MissionDefinition;
  cargo?: { resourceId: string; quantity: number };
};
type PlayerState = {
  playerId: string;
  authSubject: string;
  walletId: string | null;
  profile: { displayName: string; operatorTag: string; setupAt: number } | null;
  version: number;
  balances: Balance;
  resources: { storageCapacity: number; stored: Record<string, number> };
  ships: { starter: { shipId: string; cargoCapacity: number } };
  progression: { level: number; xp: number; unlockedAreas: string[] };
  missions: {
    active: Record<string, MissionInstance>;
    completed: Record<string, MissionInstance & { claimedAt: number }>;
    deliveryProgress: Record<string, { resourceId: string; delivered: number; goal: number; rewardClaimed: boolean }>;
  };
  proof: { latestEventHash: string | null; latestChannelBlockHash: string | null; latestAnchoredVersion: number };
};
type ReplayResult = { verified: boolean; eventCount: number; latestEventHash: string | null; finalState: PlayerState | null };
type CallBody = { op: string; args: Record<string, unknown>; ctx: Record<string, string> };
type CallEntry = {
  id: string;
  request: { method: string; url: string; headers: Record<string, string>; body: CallBody };
  response: { status: number; headers: Record<string, string>; body: unknown; timeMs: number } | null;
};

const subject = ref(`dev-player-${Math.floor(Math.random() * 10000)}`);
const playerState = ref<PlayerState | null>(null);
const missions = ref<MissionDefinition[]>([]);
const selectedMissionId = ref('luna_supply_run_001');
const replay = ref<ReplayResult | null>(null);
const message = ref('Create a player, set a profile, then run the story mission five times.');
const busy = ref(false);
const automationRunning = ref(false);
const nowSeconds = ref(Math.floor(Date.now() / 1000));
const calls = ref<CallEntry[]>([]);
const selectedCallId = ref<string | null>(null);
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

const activeMission = computed(() => Object.values(playerState.value?.missions.active ?? {})[0] ?? null);
const remainingSeconds = computed(() => Math.max(0, (activeMission.value?.endsAt ?? nowSeconds.value) - nowSeconds.value));
const selectedMission = computed(() => missions.value.find((entry) => entry.missionId === selectedMissionId.value) ?? null);
const selectedCall = computed(() => calls.value.find((entry) => entry.id === selectedCallId.value) ?? null);
const orderedCalls = computed(() => [...calls.value].reverse());
const controlsLocked = computed(() => busy.value || automationRunning.value);
const canStart = computed(() => {
  if (!playerState.value?.profile || !selectedMission.value || activeMission.value || controlsLocked.value) return false;
  if (selectedMission.value.logistics?.type === 'delivery') return dispatchableGoodsA.value > 0;
  return true;
});
const canClaim = computed(() => !!activeMission.value && remainingSeconds.value === 0 && !controlsLocked.value);
const canRunFive = computed(() => !!playerState.value?.profile && !activeMission.value && playerState.value.progression.level < 2 && !controlsLocked.value);
const apiLabel = computed(() => apiBaseUrl || 'same-origin');
const storageMode = computed(() => selectedCall.value?.response?.headers['x-chronos-storage'] || '-');
const indexMode = computed(() => selectedCall.value?.response?.headers['x-chronos-index'] || '-');
const storageCapacity = computed(() => playerState.value?.resources?.storageCapacity ?? 60);
const storageUsed = computed(() => Object.values(playerState.value?.resources?.stored ?? {}).reduce((total, quantity) => total + quantity, 0));
const storagePercent = computed(() => storageCapacity.value > 0 ? Math.min(100, Math.round((storageUsed.value / storageCapacity.value) * 100)) : 0);
const goodsAStored = computed(() => playerState.value?.resources?.stored.goods_a ?? 0);
const starterCapacity = computed(() => playerState.value?.ships?.starter.cargoCapacity ?? 30);
const deliveryProgress = computed(() => playerState.value?.missions.deliveryProgress?.goods_a_delivery_contract ?? {
  resourceId: 'goods_a',
  delivered: 0,
  goal: selectedMission.value?.logistics?.type === 'delivery' ? selectedMission.value.logistics.goal : 50,
  rewardClaimed: false,
});
const deliveryRemaining = computed(() => Math.max(0, deliveryProgress.value.goal - deliveryProgress.value.delivered));
const dispatchableGoodsA = computed(() => Math.min(starterCapacity.value, goodsAStored.value, deliveryRemaining.value));
const startActionLabel = computed(() => {
  if (selectedMission.value?.logistics?.type === 'collection') return `Collect ${selectedMission.value.logistics.quantity} Goods A`;
  if (selectedMission.value?.logistics?.type === 'delivery') return `Dispatch ${dispatchableGoodsA.value} Goods A`;
  return 'Start Selected Mission';
});
const claimActionLabel = computed(() => {
  if (activeMission.value?.definition.logistics?.type === 'collection') return 'Unload Goods';
  if (activeMission.value?.definition.logistics?.type === 'delivery') return 'Confirm Delivery';
  return 'Collect Reward';
});
const activeMissionSummary = computed(() => {
  const logistics = activeMission.value?.definition.logistics;
  if (logistics?.type === 'collection') return `Collecting ${logistics.quantity} Goods A`;
  if (logistics?.type === 'delivery') return `Delivering ${activeMission.value?.cargo?.quantity ?? 0} Goods A`;
  return 'Active mission timer';
});

let timer: number | undefined;

onMounted(() => {
  timer = window.setInterval(() => {
    nowSeconds.value = Math.floor(Date.now() / 1000);
  }, 250);
});

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);
});

async function bootstrapPlayer() {
  const response = await callOp('player.bootstrap:v1', {}, `boot-${Date.now()}`);
  playerState.value = response.result.state;
  await listMissions();
}

async function setupProfile() {
  if (!playerState.value) return;
  const response = await callOp(
    'player.setupProfile:v1',
    { expectedVersion: playerState.value.version, displayName: 'Ada Relay', operatorTag: 'ADA-01' },
    `profile-${Date.now()}`,
  );
  playerState.value = response.result.state;
}

async function loadState() {
  const response = await callOp('player.getState:v1', {});
  playerState.value = response.result.state;
}

async function listMissions() {
  const response = await callOp('mission.list:v1', {});
  missions.value = response.result.missions;
  selectedMissionId.value = missions.value.find((mission) => mission.generated)?.missionId
    ?? missions.value.find((mission) => mission.missionId === selectedMissionId.value)?.missionId
    ?? missions.value[0]?.missionId
    ?? selectedMissionId.value;
}

async function startMission() {
  if (!playerState.value) return;
  const mission = selectedMission.value;
  if (!mission) return;
  const missionArgs = mission.generated
    ? { expectedVersion: playerState.value.version, missionId: mission.missionId, mission }
    : { expectedVersion: playerState.value.version, missionId: mission.missionId };
  const response = await callOp(
    'mission.start:v1',
    missionArgs,
    `start-${Date.now()}`,
  );
  playerState.value = response.result.state;
  if (response.result.mission.cargo) {
    message.value = `Dispatched ${response.result.mission.cargo.quantity} Goods A.`;
  }
}

async function claimMission() {
  if (!playerState.value || !activeMission.value) return;
  const logistics = activeMission.value.definition.logistics;
  const response = await callOp(
    'mission.claim:v1',
    { expectedVersion: playerState.value.version, missionInstanceId: activeMission.value.missionInstanceId },
    `claim-${Date.now()}`,
  );
  playerState.value = response.result.state;
  await listMissions();
  if (logistics?.type === 'collection') message.value = `Unloaded ${logistics.quantity} Goods A into storage.`;
  if (logistics?.type === 'delivery') {
    const progress = playerState.value?.missions.deliveryProgress.goods_a_delivery_contract;
    message.value = `Delivery progress ${progress?.delivered ?? 0}/${progress?.goal ?? logistics.goal} Goods A.`;
  }
}

async function runFiveMissions() {
  automationRunning.value = true;
  try {
    selectedMissionId.value = 'luna_supply_run_001';
    for (let run = 1; run <= 5 && (playerState.value?.progression.level ?? 1) < 2; run += 1) {
      message.value = `Running story mission ${run}/5`;
      await startMission();
      while (remainingSeconds.value > 0) {
        await delay(250);
      }
      await claimMission();
    }
    await verifyReplay();
    message.value = 'Mission loop complete. The level 2 story progression is replay verified.';
  } finally {
    automationRunning.value = false;
  }
}

async function verifyReplay() {
  const response = await callOp('ledger.verifyReplay:v1', {});
  replay.value = response.result;
}

async function deleteAccount() {
  await callOp('player.deleteAccount:v1', {}, `delete-${Date.now()}`);
  playerState.value = null;
  missions.value = [];
  replay.value = null;
  selectedMissionId.value = 'luna_supply_run_001';
  message.value = 'Account object destroyed for this dev subject.';
}

async function callOp(op: string, args: Record<string, unknown>, idempotencyKey?: string) {
  busy.value = true;
  const requestId = crypto.randomUUID();
  const body: CallBody = { op, args, ctx: { requestId, ...(idempotencyKey ? { idempotencyKey } : {}) } };
  const callUrl = `${apiBaseUrl}/call`;
  const entry: CallEntry = {
    id: requestId,
    request: {
      method: 'POST',
      url: callUrl,
      headers: { Authorization: `Bearer dev:${subject.value}`, 'Content-Type': 'application/json' },
      body,
    },
    response: null,
  };
  calls.value.push(entry);
  selectedCallId.value = entry.id;
  const startedAt = performance.now();
  try {
    const response = await fetch(callUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer dev:${subject.value}` },
      body: JSON.stringify(body),
    });
    const payload = await response.json();
    entry.response = { status: response.status, headers: responseHeaders(response), body: payload, timeMs: Math.round(performance.now() - startedAt) };
    if (payload.state === 'error') throw new Error(`${payload.error.code}: ${payload.error.message}`);
    message.value = `${op} completed`;
    return payload;
  } catch (error) {
    message.value = error instanceof Error ? error.message : 'OpenCALL request failed';
    throw error;
  } finally {
    busy.value = false;
  }
}

function clearCalls() {
  calls.value = [];
  selectedCallId.value = null;
}

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function responseHeaders(response: Response): Record<string, string> {
  return {
    'x-chronos-storage': response.headers.get('x-chronos-storage') ?? '',
    'x-chronos-index': response.headers.get('x-chronos-index') ?? '',
    'x-chronos-player-object-name': response.headers.get('x-chronos-player-object-name') ?? '',
  };
}

function missionSummary(mission: MissionDefinition): string {
  if (mission.logistics?.type === 'collection') return `collect ${mission.logistics.quantity} Goods A`;
  if (mission.logistics?.type === 'delivery') return `deliver ${mission.logistics.goal} Goods A`;
  return `+${mission.reward.xp} XP`;
}

function missionEconomy(mission: MissionDefinition): string {
  const cost = mission.cost.credits ?? 0;
  const reward = mission.reward.credits ?? 0;
  const xp = mission.reward.xp ?? 0;
  return `-${cost} / +${reward} cr / +${xp} XP`;
}
</script>

<style scoped>
.shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 440px;
  background: #f4f6f8;
  color: #17202a;
}

.workspace {
  padding: 28px;
  overflow: auto;
}

.topbar {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: end;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #637083;
}

h1,
h2,
h3,
p {
  margin: 0;
}

h1 {
  font-size: 2rem;
}

h2 {
  font-size: 1rem;
}

h3 {
  font-size: 0.78rem;
  color: #637083;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.subject-control {
  min-width: 280px;
}

label,
.metric span,
dt,
.timer span,
.proof-row span {
  display: block;
  color: #637083;
  font-size: 0.78rem;
}

input {
  width: 100%;
  margin-top: 6px;
  padding: 10px 12px;
  border: 1px solid #cfd6df;
  border-radius: 6px;
  background: #ffffff;
  color: #17202a;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.metric,
.panel {
  background: #ffffff;
  border: 1px solid #dce2ea;
  border-radius: 8px;
}

.metric {
  padding: 14px;
}

.metric strong {
  display: block;
  margin-top: 4px;
  font-size: 1.2rem;
}

.flow {
  display: grid;
  gap: 16px;
}

.panel {
  padding: 16px;
}

.panel-header,
.explorer-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.version {
  color: #637083;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

button {
  border: 1px solid #1f6feb;
  background: #1f6feb;
  color: #ffffff;
  border-radius: 6px;
  padding: 10px 14px;
  font: inherit;
  font-weight: 650;
  cursor: pointer;
}

button:hover:not(:disabled) {
  background: #1558c7;
}

button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

button.small {
  padding: 7px 10px;
  font-size: 0.82rem;
  background: #eef4ff;
  color: #1751b8;
}

button.danger {
  border-color: #b42318;
  background: #b42318;
}

button.danger:hover:not(:disabled) {
  background: #8f1d15;
}

.state-list {
  display: grid;
  gap: 10px;
  margin: 14px 0 0;
}

.storage-meter {
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #e7ecf2;
  border: 1px solid #d3dbe5;
}

.storage-meter span {
  display: block;
  height: 100%;
  min-width: 0;
  background: #1f8a70;
}

.logistics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.logistics-grid div {
  padding: 12px;
  border: 1px solid #edf1f5;
  border-radius: 8px;
  background: #fbfcfe;
}

.logistics-grid span {
  display: block;
  color: #637083;
  font-size: 0.78rem;
}

.logistics-grid strong {
  display: block;
  margin-top: 4px;
  font-size: 1.1rem;
}

.state-list div,
.proof-row,
.timer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid #edf1f5;
}

dd {
  margin: 0;
  text-align: right;
}

.hash {
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.mission-list {
  display: grid;
  gap: 8px;
}

.mission {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  text-align: left;
  background: #f8fafc;
  color: #17202a;
  border-color: #dce2ea;
}

.mission.selected {
  border-color: #1f6feb;
  background: #eef4ff;
}

.mission small {
  display: block;
  margin-top: 3px;
  color: #637083;
}

.mission em {
  display: inline-flex;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #e7f7ed;
  color: #16833a;
  font-size: 0.68rem;
  font-style: normal;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cost {
  white-space: nowrap;
  color: #637083;
}

.timer strong {
  font-size: 1.2rem;
}

.ok {
  color: #16833a;
}

.message {
  margin-top: 14px;
  color: #465466;
}

.explorer {
  height: 100vh;
  overflow: auto;
  border-left: 1px solid #30363d;
  background: #0d1117;
  color: #e6edf3;
  padding: 18px;
}

.explorer h2 {
  color: #e6edf3;
}

.empty {
  min-height: 220px;
  display: grid;
  place-items: center;
  color: #8b949e;
  text-align: center;
  border: 1px dashed #30363d;
  border-radius: 8px;
  padding: 24px;
}

.call-list {
  display: grid;
  gap: 6px;
  margin-bottom: 14px;
}

.call-list button {
  display: flex;
  justify-content: space-between;
  background: #161b22;
  border-color: #30363d;
  color: #e6edf3;
  padding: 8px 10px;
}

.call-list button.selected {
  border-color: #58a6ff;
  background: #10233f;
}

.payloads {
  display: grid;
  gap: 8px;
}

pre {
  margin: 0 0 12px;
  max-height: 320px;
  overflow: auto;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #30363d;
  background: #010409;
  color: #e6edf3;
  font-size: 0.78rem;
  line-height: 1.45;
}

@media (max-width: 1020px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .explorer {
    height: auto;
    border-left: 0;
    border-top: 1px solid #30363d;
  }
}

@media (max-width: 720px) {
  .workspace {
    padding: 18px;
  }

  .topbar,
  .state-list div,
  .proof-row,
  .timer {
    display: block;
  }

  .status-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .logistics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .subject-control {
    min-width: 0;
  }

  dd {
    margin-top: 4px;
    text-align: left;
  }
}
</style>
